import {
  array,
  boolean,
  email,
  maxLength,
  minLength,
  nonEmpty,
  nonNullable,
  nullable,
  number,
  object,
  picklist,
  pipe,
  record,
  string,
  trim,
} from 'valibot';

import { DATETIME_FORMATS } from '@constants/datetimeFormats';
import { simpleEventsKeys } from '@constants/simpleEventsKeys';

import { getSimpleEmailSubscriptions } from '@helpers/getSimpleEmailSubscriptions';

export const USER_FIELDS = {
  IS_ACTIVE: 'is_active',
  NAME_FIRST: 'name_first',
  NAME_LAST: 'name_last',
  USERNAME: 'username',
  COMPANY: 'company',
  PHONE: 'phone',
  EMAIL: 'email',
  IMAGE: 'image',
  SECURITY_EMAIL: 'security_email',
  TIMEZONE: 'timezone_id',
  ALL_FACILITIES: 'all_facilities',
  LOCATIONS: 'locations',
  LANGUAGE: 'language_iso_code',
  ROLE: 'role',
  VIEW_SENSOR_DATA_PERMISSION: 'viewSensorDataPermission',
  USE_METRIC: 'use_metric',
  DATETIME_FORMAT: 'datetime_format',
  ACTION_LIST_URGENCIES: 'action_list_urgencies',
  DEFAULT_SUBSCRIBED: 'default_subscribed_to_email_subscription_settings',
  RECEIVE_OWN_EMAILS: 'receive_own_emails',
  SIMPLE_EMAIL_SUBSCRIPTIONS: 'simple_email_subscriptions',
  JOB_TITLE: 'job_title',
};

export const schemaPersonalInfoStep = object({
  [USER_FIELDS.NAME_FIRST]: pipe(string(), trim(), nonEmpty('This field is required.')),
  [USER_FIELDS.NAME_LAST]: pipe(string(), trim(), nonEmpty('This field is required.')),
  [USER_FIELDS.EMAIL]: pipe(
    string(),
    trim(),
    nonEmpty('This field is required.'),
    email('Enter correct email'),
    maxLength(255, 'Your email is too long.'),
  ),
  [USER_FIELDS.USERNAME]: pipe(string(), trim(), nonEmpty('This field is required.')),
  [USER_FIELDS.LANGUAGE]: object({
    iso_code: nonNullable(nullable(string()), 'This field is required.'),
    name: string(),
  }),
  [USER_FIELDS.COMPANY]: pipe(string(), trim(), nonEmpty('This field is required.')),
  [USER_FIELDS.PHONE]: string(),
  [USER_FIELDS.JOB_TITLE]: string(),
  [USER_FIELDS.IS_ACTIVE]: boolean(),
  [USER_FIELDS.IMAGE]: nullable(string()),
});

export const schemaFacilityStep = object({
  [USER_FIELDS.LOCATIONS]: pipe(array(number()), minLength(1)),
  [USER_FIELDS.ALL_FACILITIES]: number(),
});

export const schemaRegionAndPermissionsStep = object({
  [USER_FIELDS.TIMEZONE]: object({
    id: nonNullable(nullable(number()), 'This field is required.'),
    title: string(),
  }),
  [USER_FIELDS.USE_METRIC]: pipe(number(), nonEmpty('This field is required.')),
  [USER_FIELDS.DATETIME_FORMAT]: pipe(string(), nonEmpty('This field is required.')),
  [USER_FIELDS.ROLE]: pipe(string(), nonEmpty('This field is required.')),
  [USER_FIELDS.VIEW_SENSOR_DATA_PERMISSION]: boolean(),
});

export const schemaNotificationsStep = object({
  [USER_FIELDS.ACTION_LIST_URGENCIES]: object({ 1: boolean(), 2: boolean(), 3: boolean() }),
  [USER_FIELDS.DEFAULT_SUBSCRIBED]: boolean(),
  [USER_FIELDS.RECEIVE_OWN_EMAILS]: boolean(),
  [USER_FIELDS.SIMPLE_EMAIL_SUBSCRIPTIONS]: record(picklist(simpleEventsKeys), boolean()),
});

export const userDefaultValues = (
  t,
  userData,
  simpleEventsDictionary,
  structureData,
  languageList,
  timezoneList,
) => {
  const user = userData?.user;
  const userRole = userData?.userRole;
  const userViewSensorDataPermission = userData?.viewSensorDataPermission;

  const currentLanguage = languageList?.find((lang) => lang.iso_code === user?.language_iso_code);
  const currentTimezone = timezoneList?.find((zone) => zone.id === user?.timezone_id);

  const getUserLocations = () => {
    if (+user?.all_facilities) {
      return structureData?.facilities.map((facility) => facility.id);
    }

    return Object.keys(user?.locations).map((val) => +val);
  };

  const getAIUrgencies = () => {
    const aiUrgencies = { 1: false, 2: false, 3: true };

    if (user?.id) {
      user?.dashboard_push_setting_urgencies.forEach((urgency) => {
        aiUrgencies[urgency.id] = true;
      });
    }

    return aiUrgencies;
  };

  return {
    [USER_FIELDS.IS_ACTIVE]: user?.id ? !!user?.is_active : true,
    [USER_FIELDS.NAME_FIRST]: user?.name_first || '',
    [USER_FIELDS.NAME_LAST]: user?.name_last || '',
    [USER_FIELDS.USERNAME]: user?.username || '',
    [USER_FIELDS.COMPANY]: user?.company || '',
    [USER_FIELDS.PHONE]: user?.phone || '',
    [USER_FIELDS.JOB_TITLE]: user?.job_title || '',
    [USER_FIELDS.EMAIL]: user?.email || '',
    [USER_FIELDS.IMAGE]: user?.image || user?.userpic || null,
    [USER_FIELDS.SECURITY_EMAIL]: user?.security_email || '',
    [USER_FIELDS.TIMEZONE]: currentTimezone || { id: 21, title: t('(UTC-05:00) Eastern Time (US & Canada)') },
    [USER_FIELDS.ALL_FACILITIES]: user?.id ? +user?.all_facilities : 0,
    [USER_FIELDS.LOCATIONS]: user?.id ? getUserLocations() : [],
    [USER_FIELDS.LANGUAGE]: currentLanguage || { iso_code: 'en', name: t('English') },
    [USER_FIELDS.ROLE]: userRole || 'dashboardEditor',
    [USER_FIELDS.VIEW_SENSOR_DATA_PERMISSION]: user?.id ? !!userViewSensorDataPermission : false,
    [USER_FIELDS.USE_METRIC]: user?.id ? user?.use_metric : 1,
    [USER_FIELDS.DATETIME_FORMAT]: user?.personal_settings?.datetime_format || DATETIME_FORMATS.us.dateTime,
    [USER_FIELDS.ACTION_LIST_URGENCIES]: getAIUrgencies(),
    [USER_FIELDS.DEFAULT_SUBSCRIBED]: user?.id
      ? !!user?.default_subscribed_to_email_subscription_settings
      : true,
    [USER_FIELDS.RECEIVE_OWN_EMAILS]: user?.id ? !!user?.receive_own_emails : true,
    [USER_FIELDS.SIMPLE_EMAIL_SUBSCRIPTIONS]: getSimpleEmailSubscriptions(
      simpleEventsDictionary,
      user?.email_subscriptions,
    ),
  };
};
